import { useState } from 'react'

import { ReconciliationTransactionDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  ConfirmReconciliationSuggestionDocument,
  ConfirmReconciliationSuggestionMutation,
  RejectReconciliationSuggestionDocument,
  RejectReconciliationSuggestionMutation,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Invoice {
  id: string
  documentNumber?: string | null
  totalAmountCents?: number | null
  currency?: string | null
  documentDate?: string | null
  dueDate?: string | null
  vendor?: {
    legalName?: string | null
  } | null
  taxes: {
    amountCents?: number | null
    percentage?: number | null
  }[]
}

interface Transaction {
  bookingDate: string
  amountCents: number
  description?: string | null
}

export interface ReconciliationSuggestionsProps {
  id: string
  invoice?: Invoice | null
  transaction?: Transaction
}

export const useReconciliationSuggestion = ({
  onTransactionFullyReconciled,
  suggestion,
  companyId,
}: {
  onTransactionFullyReconciled?: () => void
  suggestion?: ReconciliationSuggestionsProps
  companyId: string
}) => {
  const [firstSuggestion, setFirstSuggestion] = useState(true)

  const [confirm, { loading: loadingConfirm }] = useMutation(
    ConfirmReconciliationSuggestionDocument,
    {
      extractorFn: (data: ConfirmReconciliationSuggestionMutation) =>
        data.bankingMutations.confirmBankingReconciliationSuggestion,
      refetchQueries: [ReconciliationTransactionDataTableDocument],
    }
  )
  const [reject, { loading: loadingReject }] = useMutation(RejectReconciliationSuggestionDocument, {
    extractorFn: (data: RejectReconciliationSuggestionMutation) =>
      data.bankingMutations.rejectBankingReconciliationSuggestion,
  })

  const confirmSuggestion = async () => {
    if (!suggestion) return

    const unhandledResult = await confirm({
      variables: {
        id: suggestion.id,
        companyId: companyId,
      },
    })

    const result = handleResult(unhandledResult, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('banking.reconciliations.submit_messages.creating_error'),
      })
    })

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('banking.reconciliations.submit_messages.creating_success'),
      })
      const updatedTransaction = result.data.reconciliationSuggestion?.transaction

      if (updatedTransaction?.fullyReconciledAt && onTransactionFullyReconciled) {
        onTransactionFullyReconciled()
      }
    }
  }

  const rejectSuggestion = async () => {
    if (!suggestion) return

    setFirstSuggestion(false)

    const unhandledResult = await reject({
      variables: {
        id: suggestion.id,
        companyId: companyId,
      },
    })

    const result = handleResult(unhandledResult, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('banking.reconciliations.suggestions.rejecting_suggestion_error'),
      })
    })

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('banking.reconciliations.suggestions.rejecting_suggestion_success'),
      })
    }
  }

  return {
    isLoading: loadingConfirm || loadingReject,
    firstSuggestion,
    confirmSuggestion,
    rejectSuggestion,
  }
}
